'use client';

import { useAuth } from '@clerk/clerk-react';
import type { InfiniteData } from '@tanstack/react-query';
import { useInfiniteQuery } from '@tanstack/react-query';

import {
  type HumanReviewFacets,
  type HumanReviewRequest,
  type HumanReviewRequestFilter,
  type HumanReviewRequestsResult,
  listHumanReviewRequests,
} from '@/lib/admin/client';
import { humanReviewRequestsQueryKey } from '@/lib/admin/queryKeys';

const DEFAULT_PAGE_SIZE = 20;

type RequestPages = InfiniteData<HumanReviewRequestsResult>;

// The routes 404 when MIND_HUMAN_REVIEW_ENABLED is off; the client maps that to
// a 'disabled' page so the panel can explain the dark flag.
export function humanReviewDisabled(data: RequestPages | undefined): boolean {
  return data?.pages[0]?.status === 'disabled';
}

export function humanReviewRequests(
  data: RequestPages | undefined
): HumanReviewRequest[] {
  if (!data) return [];
  // flatMap wraps a non-array return, so a page missing `requests` would yield a
  // hole ([undefined]) that every consumer then dereferences. Coalesce here.
  return data.pages.flatMap((page) =>
    page.status === 'available' ? (page.data.requests ?? []) : []
  );
}

// Facets are recomputed per request against the active filters; use the newest
// page that carried them.
export function humanReviewFacets(
  data: RequestPages | undefined
): HumanReviewFacets | undefined {
  if (!data) return undefined;
  for (let index = data.pages.length - 1; index >= 0; index -= 1) {
    const page = data.pages[index];
    if (page.status === 'available' && page.data.facets)
      return page.data.facets;
  }
  return undefined;
}

export function useHumanReviewRequests(
  filter: HumanReviewRequestFilter,
  { pageSize = DEFAULT_PAGE_SIZE }: { pageSize?: number } = {}
) {
  const { getToken, isLoaded, isSignedIn, sessionId, userId } = useAuth();
  const ready = isLoaded && !!isSignedIn && !!userId && !!sessionId;

  return useInfiniteQuery<HumanReviewRequestsResult>({
    queryKey: humanReviewRequestsQueryKey({ userId, sessionId }, filter),
    enabled: ready,
    retry: false,
    initialPageParam: undefined,
    queryFn: async ({ pageParam }) => {
      const token = await getToken();
      if (!token) throw new Error('Missing auth token');
      return listHumanReviewRequests(token, {
        state: filter.state === 'all' ? undefined : filter.state,
        subjectType: filter.subjectType ?? undefined,
        priority: filter.priority ?? undefined,
        decisionSource: filter.decisionSource ?? undefined,
        sort: filter.sort,
        limit: pageSize,
        cursor: typeof pageParam === 'string' ? pageParam : undefined,
      });
    },
    getNextPageParam: (lastPage) =>
      lastPage.status === 'available' && lastPage.data.hasMore
        ? lastPage.data.nextCursor
        : undefined,
  });
}
